import { Box, Button, CloseButton, Flex, Icon, InfoIcon, Text, useColorModeValue } from '@chaine/keychaine'
import { motion } from 'framer-motion'
import React, { memo, useCallback, useEffect, useState } from 'react'

interface IDeprecationBannerProps {
  /** Auto redirect delay in ms.*/
  autoRedirectDelayMs?: number
  /** Optional description override */
  description?: string
  /** Whether the banner can be manually dismissed */
  dismissible?: boolean
  /** URL to redirect users to the new app */
  newAppUrl: string
  /** Optional redirect handler override for tests */
  onRedirect?: (newAppUrl: string) => void
  /** Optional title override */
  title?: string
}

const MotionBox = motion(Box)

/**
 * DeprecationBanner displays a beautiful popup notification to users that the app will be deprecated
 */
export const DeprecationBanner = memo(
  ({
    newAppUrl,
    autoRedirectDelayMs,
    dismissible = true,
    title = 'A New Version of Chain is Available',
    description,
    onRedirect
  }: IDeprecationBannerProps) => {
    const isForcedRedirectMode = typeof autoRedirectDelayMs === 'number' && autoRedirectDelayMs > 0
    const canDismiss = dismissible && !isForcedRedirectMode

    // Check sessionStorage before initial render to prevent flashing in manual mode.
    const [isVisible, setIsVisible] = useState(() => {
      if (!canDismiss) return true
      const dismissed = sessionStorage.getItem('deprecation-banner-dismissed')
      return dismissed !== 'true'
    })
    const [remainingSeconds, setRemainingSeconds] = useState(() =>
      isForcedRedirectMode ? Math.ceil((autoRedirectDelayMs || 0) / 1000) : 0
    )

    const bgColor = useColorModeValue('white', 'gray.800')
    const borderColor = useColorModeValue('orange.400', 'orange.500')
    const textColor = useColorModeValue('gray.800', 'white')
    const secondaryTextColor = useColorModeValue('gray.600', 'gray.300')
    const iconColor = useColorModeValue('orange.500', 'orange.400')
    const hoverBgColor = useColorModeValue('gray.100', 'gray.700')

    const handleClose = () => {
      if (!canDismiss) return
      setIsVisible(false)
      sessionStorage.setItem('deprecation-banner-dismissed', 'true')
    }

    const handleRedirect = useCallback(() => {
      if (onRedirect) {
        onRedirect(newAppUrl)
        return
      }
      window.location.assign(newAppUrl)
    }, [newAppUrl, onRedirect])

    useEffect(() => {
      if (!isForcedRedirectMode || !isVisible || !autoRedirectDelayMs) return
      setRemainingSeconds(Math.ceil(autoRedirectDelayMs / 1000))
      const countdownInterval = window.setInterval(() => {
        setRemainingSeconds((previous) => (previous > 1 ? previous - 1 : 1))
      }, 1000)
      const redirectTimer = window.setTimeout(() => {
        handleRedirect()
      }, autoRedirectDelayMs)

      return () => {
        clearInterval(countdownInterval)
        clearTimeout(redirectTimer)
      }
    }, [autoRedirectDelayMs, handleRedirect, isForcedRedirectMode, isVisible])

    if (!isVisible) {
      return null
    }

    return (
      <MotionBox
        position="fixed"
        bottom={{ base: 4, md: 6 }}
        right={{ base: 4, md: 6 }}
        zIndex={9999}
        maxW={{ base: 'calc(100vw - 32px)', md: '420px' }}
        initial={{ opacity: 0, scale: 0.95, y: 50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 50 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <Box
          bg={bgColor}
          borderRadius="xl"
          boxShadow="2xl"
          borderWidth="2px"
          borderColor={borderColor}
          overflow="hidden"
        >
          {/* Orange accent bar */}
          <Box h="4px" bg="linear-gradient(90deg, orange.400, orange.600)" />

          <Box p={5}>
            <Flex justify="space-between" align="start" mb={3}>
              <Flex align="center" gap={2}>
                <Icon as={InfoIcon} color={iconColor} />
                <Text fontSize="lg" fontWeight="bold" color={textColor}>
                  {title}
                </Text>
              </Flex>
              {canDismiss && <CloseButton size="sm" onClick={handleClose} color={secondaryTextColor} />}
            </Flex>

            {description ? (
              <Text fontSize="sm" color={secondaryTextColor} mb={4} lineHeight="1.6">
                {description}
              </Text>
            ) : (
              <Text fontSize="sm" color={secondaryTextColor} mb={4} lineHeight="1.6">
                We&apos;ve transitioned our primary platform to <strong>Chain v2</strong>. While this legacy version
                remains accessible, we encourage you to switch to v2 for the latest features, improved performance, and
                a more modern experience.
              </Text>
            )}

            {isForcedRedirectMode && (
              <Text fontSize="xs" color={secondaryTextColor} mb={3}>
                Redirecting in {remainingSeconds} second{remainingSeconds === 1 ? '' : 's'}...
              </Text>
            )}

            <Flex gap={2} flexDirection={{ base: 'column', sm: 'row' }}>
              <Button
                size="sm"
                colorScheme="orange"
                onClick={handleRedirect}
                flex={1}
                fontWeight="semibold"
                _hover={{
                  boxShadow: 'lg',
                  transform: 'translateY(-2px)'
                }}
                transition="all 0.2s"
              >
                Go to v2 →
              </Button>
              {canDismiss && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleClose}
                  colorScheme="gray"
                  _hover={{
                    bg: hoverBgColor
                  }}
                >
                  Dismiss
                </Button>
              )}
            </Flex>
          </Box>
        </Box>
      </MotionBox>
    )
  }
)

DeprecationBanner.displayName = 'DeprecationBanner'
